// HA instance create. The llat_secret_id picker is an Autocomplete
// over the tenant's existing tenant_secrets — operators select by ref
// instead of pasting a UUID. The composite FK on the backend enforces
// same-tenant, but pre-validation here keeps the UX honest.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { haInstances } from "../../api/endpoints/haInstances";
import { tenantSecrets } from "../../api/endpoints/tenantSecrets";
import { qk } from "../../api/queryKeys";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { AutocompleteField } from "../../components/forms/AutocompleteField";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import type { HaInstanceCreate } from "../../api/types/haInstance";
import type { TenantSecretOut } from "../../api/types/tenantSecret";

interface HaInstanceCreateFormProps {
  accountSlug: string;
  tenantSlug: string;
  onDone: () => void;
  onPendingChange?: (pending: boolean) => void;
}

interface FormState {
  base_url: string;
  llat_secret: TenantSecretOut | null;
}

const EMPTY: FormState = { base_url: "", llat_secret: null };

export function HaInstanceCreateForm({
  accountSlug,
  tenantSlug,
  onDone,
  onPendingChange,
}: HaInstanceCreateFormProps) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<FormState>(EMPTY);

  const secretsQuery = useQuery({
    ...adminQueryOptions,
    queryKey: qk.tenantSecrets(accountSlug, tenantSlug),
    queryFn: () =>
      tenantSecrets.list(accountSlug, tenantSlug).then((r) => r.data),
  });

  const secretOptions = useMemo(
    () => secretsQuery.data ?? [],
    [secretsQuery.data],
  );

  const mutation = useMutation({
    mutationFn: () => {
      // B.1.7: llat_secret is optional. The backfill CLI creates rows
      // with NULL llat for legacy tenants whose LLATs live on the edge
      // box; the UI mirrors that — operator can create the HA instance
      // first and link an LLAT later once a tenant_secret exists.
      const body: HaInstanceCreate = {
        base_url: values.base_url,
        llat_secret_id: values.llat_secret?.id ?? null,
      };
      return haInstances.create(accountSlug, tenantSlug, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.haInstances(accountSlug, tenantSlug),
      });
      setValues(EMPTY);
      onDone();
    },
  });

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const baseUrlError =
    values.base_url === "" || /^https?:\/\//.test(values.base_url)
      ? null
      : "Must be an http(s) URL.";

  const secretsLoadFailed = secretsQuery.error !== null && secretsQuery.error !== undefined;
  const noSecrets =
    !secretsQuery.isPending && !secretsLoadFailed && secretOptions.length === 0;

  // B.1.7: llat_secret is optional. Form submits with URL only when no
  // secret is selected; backend persists llat_secret_id=null. A
  // secretsLoadFailed state does NOT block submit — URL-only is the
  // norm for legacy/backfilled tenants (Abdullah's LLAT lives on the
  // edge box, not Hub), so the operator should be able to create the
  // HA instance even when the picker can't load. ErrorBanner above
  // keeps the failure visible; operator links the LLAT later when the
  // secrets API comes back.
  const isValid =
    values.base_url !== "" &&
    /^https?:\/\//.test(values.base_url);

  return (
    <FormShell
      submitLabel="Create HA instance"
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid) return;
        mutation.mutate();
      }}
      onCancel={onDone}
      pending={mutation.isPending}
      error={mutation.error}
      errorTitle="Create HA instance failed"
    >
      {secretsLoadFailed && (
        <ErrorBanner
          error={secretsQuery.error}
          title="Couldn't load tenant_secrets for the picker"
        />
      )}
      <TextField
        label="HA base URL"
        type="url"
        value={values.base_url}
        onChange={(base_url) => setValues((v) => ({ ...v, base_url }))}
        required
        placeholder="https://ha.tenant.example/"
        helper="The base URL Hub will talk to for this HA instance."
        error={baseUrlError}
      />
      <AutocompleteField<TenantSecretOut>
        label="LLAT secret (optional)"
        items={secretOptions}
        value={values.llat_secret}
        onSelect={(llat_secret) => setValues((v) => ({ ...v, llat_secret }))}
        getKey={(s) => s.id}
        getLabel={(s) => s.ref}
        getSecondary={(s) => `KEK v${s.encryption_key_version}`}
        placeholder={
          secretsLoadFailed
            ? "Secrets unavailable — see error above"
            : secretsQuery.isPending
              ? "Loading secrets…"
              : noSecrets
                ? "No secrets yet — leave unlinked"
                : "Pick the llat tenant_secret (or leave unlinked)"
        }
        disabled={noSecrets || secretsLoadFailed}
        helper={
          secretsLoadFailed
            ? undefined
            : noSecrets
              ? "Leave unlinked for now; create a tenant_secret on the Secrets tab and link it later."
              : "Optional. Same-tenant only; backend rejects cross-tenant llat_secret_id."
        }
      />
    </FormShell>
  );
}
